/** A low-poly hedgerow oak whose silhouette holds up when the player gets near. */
import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

export interface FieldTreeOptions {
  readonly seed?: number;
}

const DEFAULT_SEED = 317;

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function cylinderBetween(
  from: THREE.Vector3,
  to: THREE.Vector3,
  radiusTop: number,
  radiusBottom: number,
  sides: number,
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const direction = to.clone().sub(from);
  const mesh = part(new THREE.CylinderGeometry(radiusTop, radiusBottom, direction.length(), sides), material, name);
  mesh.position.copy(from).addScaledVector(direction, 0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  return mesh;
}

function irregularLobe(seed: number): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(1, 1);
  const position = geometry.getAttribute('position');
  const random = mulberry32(seed);
  for (let index = 0; index < position.count; index += 1) {
    const x = position.getX(index);
    const y = position.getY(index);
    const z = position.getZ(index);
    const radial = 0.96 + random() * 0.08;
    position.setXYZ(index, x * radial, y * (0.97 + random() * 0.06), z * radial);
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

/** Build a deterministic field tree; its default seed is 317. */
export function buildFieldTree(options: FieldTreeOptions = {}): THREE.Group {
  const random = mulberry32(options.seed ?? DEFAULT_SEED);
  const group = new THREE.Group();
  group.name = 'field-tree';
  const bark = surface('organic', 0x4a4038);
  const shadow = surface('organic', 0x3a5742);
  const dawn = surface('organic', 0x4f8f52);

  const trunkAxis = new THREE.Vector3(0.08, 2.08, 0.04).normalize();
  // A tilted cylinder's lower cap projects below its centre. Starting it by
  // r*sin(lean) above zero keeps the physical lowest rim, not an imaginary axis,
  // at ground contact.
  const trunkStart = new THREE.Vector3(0, 0.42 * Math.hypot(trunkAxis.x, trunkAxis.z), 0);
  const trunkEnd = trunkStart.clone().addScaledVector(trunkAxis, 2.08);
  group.add(cylinderBetween(trunkStart, trunkEnd, 0.20, 0.46, 7, bark, 'tapered-trunk'));

  const boughs = [
    { start: trunkStart.clone().lerp(trunkEnd, 0.72), end: new THREE.Vector3(1.62, 3.44, 0.48), top: 0.15, bottom: 0.28, name: 'east-scaffold' },
    { start: trunkStart.clone().lerp(trunkEnd, 0.63), end: new THREE.Vector3(-1.65, 3.25, -0.46), top: 0.14, bottom: 0.26, name: 'west-scaffold' },
    { start: trunkStart.clone().lerp(trunkEnd, 0.82), end: new THREE.Vector3(0.34, 3.72, -1.40), top: 0.12, bottom: 0.22, name: 'front-scaffold' },
    { start: trunkStart.clone().lerp(trunkEnd, 0.92), end: new THREE.Vector3(-0.62, 4.12, 0.92), top: 0.11, bottom: 0.19, name: 'high-scaffold' },
  ] as const;
  const canopy = [
    // Separate shoulders leave real dips in the lower edge and keep the scaffold
    // limbs visible. Whole-lobe values read as planes of foliage, not spots.
    { at: new THREE.Vector3(-1.34, 4.18, 0.02), scale: new THREE.Vector3(1.30, 1.16, 1.42), material: shadow, name: 'west-shoulder' },
    { at: new THREE.Vector3(0.70, 4.02, -0.18), scale: new THREE.Vector3(1.60, 1.08, 1.62), material: dawn, name: 'east-shoulder' },
    { at: new THREE.Vector3(-0.34, 5.02, 0.32), scale: new THREE.Vector3(1.40, 1.28, 1.42), material: shadow, name: 'rear-crown' },
    { at: new THREE.Vector3(1.34, 5.12, 0.20), scale: new THREE.Vector3(1.00, 1.18, 1.10), material: dawn, name: 'high-east-crown' },
    { at: new THREE.Vector3(-0.80, 5.78, 0.02), scale: new THREE.Vector3(0.96, 0.82, 1.00), material: shadow, name: 'top-crown' },
  ] as const;
  for (const bough of boughs) {
    bough.end.x += (random() - 0.5) * 0.12;
    bough.end.z += (random() - 0.5) * 0.12;
    group.add(cylinderBetween(bough.start, bough.end, bough.top, bough.bottom, 5, bark, bough.name));
  }
  for (const [index, crown] of canopy.entries()) {
    crown.at.x += (random() - 0.5) * 0.14;
    crown.at.z += (random() - 0.5) * 0.14;
    const mass = part(irregularLobe(0x4100 + index * 0x31), crown.material, crown.name);
    mass.position.copy(crown.at);
    mass.scale.copy(crown.scale);
    mass.rotation.set((random() - 0.5) * 0.18, random() * Math.PI, (random() - 0.5) * 0.18);
    group.add(mass);
  }

  const bounds = new THREE.Box3().setFromObject(group);
  group.position.set(
    -(bounds.min.x + bounds.max.x) / 2,
    // ── Lifted to ground contact, and it was pinned to 0 ──────────────────────
    //
    // This was a literal `0`, on the reasonable-sounding grounds that the trunk is
    // built standing on the origin. It is not: the trunk **leans**, and a leaning
    // cylinder's lowest vertex is below the pivot it leans about — this asset
    // measured `minY = −0.0047`, which is 4.7 mm underground against a contract
    // that allows 1 mm.
    //
    // It went unnoticed because the origin/footprint gate **aborts at the first
    // violating asset** (D-098), and `grass-tuft`'s stale footprint was failing
    // ahead of it — one failure hiding another, which is the exact pattern that
    // cost session 24 several round trips. Both were fixed in one pass because both
    // were measured in one pass.
    -bounds.min.y,
    -(bounds.min.z + bounds.max.z) / 2,
  );
  return group;
}

export const fieldTreeAsset: AssetDefinition = {
  id: 'field-tree',
  label: 'Field tree',
  description: 'An asymmetrical distant hedgerow oak, made for lifting haze.',
  footprint: { width: 5.29, height: 6.18, length: 4.10 },
  create: (): THREE.Group => buildFieldTree(),
};
